/**
 * Billing API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * DB types come from @aica/database repositories.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { subscriptionTierSchema, safeRedirectUrl } from './stripe';

// ============================================================================
// Enums
// ============================================================================

/**
 * Credit Source Types
 *
 * Defines how manual credits are added to organization accounts:
 * - wire_transfer: Bank wire transfer payment from organization
 * - cash: Cash payment (rare, for special cases or offline transactions)
 * - promo: Promotional credit (marketing campaigns, trials, partnerships)
 * - adjustment: Manual adjustment by admin (corrections, refunds, goodwill credits)
 *
 * Used in createManualCreditRequestSchema to track credit origin.
 * Each source may have different approval workflows and audit requirements.
 */
export const creditSourceSchema = z.enum([
  'wire_transfer',
  'cash',
  'promo',
  'adjustment',
]);
/**
 * ✅ FIX Phase 14: Unified cost type taxonomy
 *
 * Accepts both naming conventions:
 * - Business-level: transcription, analysis (user-friendly)
 * - Technical-level: asr, llm (provider-centric)
 *
 * Both are valid in the database, this schema supports reading both.
 * New code should prefer business-level names (transcription, analysis).
 */
export const costTypeSchema = z.enum([
  // Business-level names (preferred for new code)
  'transcription',
  'analysis',
  'storage',
  'email',
  'webhook',
  'usage',
  // Technical-level names (legacy, still valid)
  'asr', // same as transcription
  'llm', // same as analysis
  'llm_script_adherence', // script adherence analysis
  'script_adherence', // alias
  // Настроечный разбор скрипта: клиенту не выставляется (решение владельца,
  // #509). Тип зависит от ПЛАТЕЛЬЩИКА, и оба варианта нужны именно поэтому.
  //
  // Наш ключ → счёт выставят НАМ, значит расход обязан попасть в отчёты:
  // тип ПЛОСКИЙ. Все шесть отчётных предикатов вырезают `provider_cost%` как
  // «отходы ретрая» (`CostRepository.ts:184,305,364,412`,
  // `UsageChargeRepository.ts:361,376`), и строка с таким префиксом не попала
  // бы ровно в те отчёты, ради которых её и пишут.
  'script_parse',
  // Ключ ОРГАНИЗАЦИИ → платит она, и обратное тоже верно: плоский тип занёс бы
  // её трату в наши расходы и занизил маржу. Строка нужна для разбора, в
  // отчёты — нет, поэтому ровно тот префикс, что эти предикаты вырезают.
  'provider_cost.script_parse_byok',
  // AI-ассистент (docs/plans/2026-09-11-assistant.md §7.5) — та же пара по
  // плательщику, что и у разбора скрипта, и по той же причине. Клиенту вопрос
  // не выставляется (включён в тариф), но каждая оплаченная попытка — строка
  // `costs`: на нашем ключе — плоский тип, чтобы расход попал в маржу и COGS;
  // на ключе организации — префикс `provider_cost`, который отчётные
  // предикаты вырезают.
  'assistant',
  'provider_cost.assistant_byok',
  // Кастомные критерии (#860, спека §3.4) — та же пара по плательщику и по той
  // же причине. Клиенту шаг не выставляется (входит в цену минуты), но каждый
  // оплаченный вызов — строка `costs`: на нашем ключе тип ПЛОСКИЙ, иначе расход
  // выпал бы из маржи и COGS (шесть отчётных предикатов вырезают
  // `provider_cost%`); на ключе организации — тот самый префикс.
  'criteria',
  'provider_cost.criteria_byok',
  // Проверка формулировки критерия на выборке (#861, план §3.4) — та же пара по
  // плательщику. Отдельный тип, а не `criteria`: у оценки расход входит в
  // себестоимость записи, а проверка — нажатие кнопки в настройках, от которого
  // записи не остаётся. Одним типом трату на настройку было бы нечем отделить от
  // себестоимости того звонка, чей транскрипт попал в выборку.
  'criteria_trial',
  'provider_cost.criteria_trial_byok',
  // Помощник «своими словами» и правка по примерам (#1416) — та же пара по
  // плательщику и по той же причине, что у проверки: нажатие кнопки в
  // настройках, от которого записи не остаётся.
  'criteria_draft',
  'provider_cost.criteria_draft_byok',
  'criteria_revise',
  'provider_cost.criteria_revise_byok',
  // Шаг «голос клиента» (#843, #844, спека §2) — та же пара по плательщику и
  // по той же причине, что у критериев. Клиенту шаг не выставляется (цена
  // минуты не меняется), но каждый оплаченный вызов — строка `costs`: на нашем
  // ключе тип ПЛОСКИЙ, иначе расход выпал бы из маржи и COGS; на ключе
  // организации — префикс `provider_cost`, который отчётные предикаты вырезают.
  'voice_extraction',
  'provider_cost.voice_extraction_byok',
  // Черновик FAQ из вопросов клиентов (#843, спека §8 п. 6) — нажатие кнопки,
  // а не шаг записи: отдельный тип, чтобы трату на черновик не смешать с
  // себестоимостью извлечения. Та же пара по плательщику и по той же причине.
  'voice_faq_draft',
  'provider_cost.voice_faq_draft_byok',
  // Стартовое предложение набора тем (#845, PR 4, спека §2 Р1) — нажатие
  // кнопки, а не шаг записи, и клиенту не выставляется (платит платформа).
  // Та же пара по плательщику и по той же причине, что у черновика FAQ.
  'theme_bootstrap',
  'provider_cost.theme_bootstrap_byok',
  // Недельная сводка новых тем (#845, PR 5b, спека §4) — крон, а не шаг
  // записи, и клиенту не выставляется (платит платформа). Та же пара по
  // плательщику и по той же причине, что у стартового предложения.
  'theme_proposals',
  'provider_cost.theme_proposals_byok',
  // Шаг «состояние сделки» (#766, план PR2 D10, ревизия v3 E9) — та же пара
  // по плательщику и по той же причине, что у критериев. Клиенту шаг не
  // выставляется, но каждый оплаченный вызов — строка `costs`: на нашем ключе
  // тип ПЛОСКИЙ (в маржу и COGS); на ключе организации (с выпуска, PR5) —
  // префикс `provider_cost`, который отчётные предикаты вырезают.
  'deal_state',
  'provider_cost.deal_state_byok',
  // Черновик сообщения клиенту в карточке сделки (#1675, спека
  // docs/plans/2026-10-14-deal-client-message-draft.md §3 M1) — нажатие
  // кнопки, а не шаг записи: отдельный тип, чтобы трату на черновики не
  // смешать с себестоимостью состояния сделки. Клиенту не выставляется; та же
  // пара по плательщику и по той же причине, что у черновика FAQ.
  'deal_client_message',
  'provider_cost.deal_client_message_byok',
  // Фаза стадии сделки по названиям стадий (#1206, спека
  // docs/plans/2026-10-07-ai-stage-phases.md, В3) — всегда на НАШЕМ ключе,
  // даже у организации со своим, и клиенту не выставляется: тип ПЛОСКИЙ (в
  // маржу и COGS). Пары `_byok` нет — ключ организации здесь не берётся.
  'stage_phase_classify',
  // Эмбеддинги поискового индекса (#756): звонок, чат, пересборка. Всегда на
  // нашем ключе и клиенту не выставляются — индекс входит в цену минуты, — но
  // каждый оплаченный запрос обязан попасть в маржу и COGS, поэтому тип
  // ПЛОСКИЙ. Пары `_byok` нет: ключа организации у эмбеддингов не бывает.
  'embedding',
  // Provider-only costs (not charged to client, used for reconciliation)
  'provider_cost.analysis',
  'provider_cost.categorization',
  'provider_cost.script_adherence',
  'provider_cost.transcription',
  'usage_debit.categorization', // categorize-step cost tracking
]);

/**
 * Billable Operation Types
 *
 * Primary categories for cost estimation and usage tracking:
 * - transcription: Audio-to-text conversion (ASR provider costs)
 *   - Billed by audio duration (minutes)
 *   - Examples: ElevenLabs Scribe v2
 * - analysis: LLM-based content analysis (LLM provider costs)
 *   - Billed by token usage (input + output tokens)
 *   - Examples: GPT-5-mini quality scoring, sentiment analysis
 *
 * Used in estimateQuerySchema for cost prediction before processing.
 * Maps to costTypeSchema business-level names for actual billing.
 */
export const operationTypeSchema = z.enum(['transcription', 'analysis']);

// ============================================================================
// Credits Schemas
// ============================================================================

export const createManualCreditRequestSchema = z
  .object({
    amount: z.number().positive(),
    currency: z.string().default('USD'),
    source: creditSourceSchema,
    sourceReference: z.string().optional(),
    description: z.string().min(1),
    attachmentUri: z.string().url().optional(),
    // NOTE: the audit actor (`createdBy`) is NOT a client input — it is derived
    // from the authenticated session in the route handler. Accepting it from the
    // body let any caller forge the creator of a credit (audit-trail integrity).
  })
  .strict();

export const manualCreditResponseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string(),
  amount: z.number(),
  currency: z.string(),
  source: creditSourceSchema,
  sourceReference: z.string().nullable(),
  description: z.string(),
  attachmentUri: z.string().nullable(),
  ledgerEntryId: z.string().uuid().nullable(),
  createdBy: z.string(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
});

export const createCreditResponseSchema = z.object({
  manualCredit: manualCreditResponseSchema,
  message: z.string().optional(),
});

export const listCreditsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const listCreditsResponseSchema = z.object({
  credits: z.array(manualCreditResponseSchema),
  total: z.number().int(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
});

// ============================================================================
// Budget Schemas
// ============================================================================

export const updateBudgetLimitRequestSchema = z
  .object({
    monthlyLimitCents: z.number().nonnegative(),
    notifyThresholdPercent: z.number().int().min(0).max(100).default(80),
    notifyEmails: z.array(z.string().email()).default([]),
  })
  .strict();

export const budgetLimitResponseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string(),
  monthlyLimit: z.number(),
  warningThresholdPercent: z.number().int().nullable(),
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  notifyEmails: z.array(z.string().email()),
  updatedBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const getBudgetResponseSchema = z.object({
  success: z.boolean().optional(),
  budgetLimit: budgetLimitResponseSchema.nullable(),
});

export const updateBudgetResponseSchema = z.object({
  budgetLimit: budgetLimitResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Summary Schemas
// ============================================================================

const periodDateParam = z
  .union([
    z.string().datetime(),
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  ])
  .optional();

export const summaryQuerySchema = z.object({
  periodStart: periodDateParam,
  periodEnd: periodDateParam,
  /**
   * Считать ли разбивку расхода по котлам (`usage.minuteSplit`).
   *
   * Опционально, потому что она стоит отдельного полного скана
   * `usage_charges` с тремя разборами JSONB, а читает её один экран биллинга.
   * Эту же сводку запрашивают четыре дашборда и настройки бюджета — им
   * разбивка не нужна, и платить за неё на каждой загрузке дашборда было бы
   * ровно тем обменом, против которого написан докблок `getOrgMinuteSplit`.
   */
  includeMinuteSplit: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((value) => value === true || value === 'true' || value === '1')
    .optional()
    .default(false),
});

export const billingSummaryResponseSchema = z.object({
  balance: z.number(),
  usage: z.number(),
  budgetLimit: z
    .object({
      monthlyLimitCents: z.number(),
      warningThresholdPercent: z.number().int(),
    })
    .nullable(),
});

// ============================================================================
// Balance Schemas
// ============================================================================

export const balanceQuerySchema = z.object({
  currency: z.string().length(3).default('USD'),
});

export const balanceResponseSchema = z.object({
  balance: z.object({
    orgId: z.string(),
    balance: z.number(),
    currency: z.string(),
    lastUpdated: z.string(),
  }),
});

// ============================================================================
// Estimate Schemas
// ============================================================================

export const estimateQuerySchema = z.object({
  operation: operationTypeSchema,
  duration: z.coerce.number().positive().optional(),
});

export const estimateResponseSchema = z.object({
  success: z.boolean().optional(),
  estimate: z.object({
    operation: operationTypeSchema,
    estimatedCost: z.number(),
    estimatedChargeCents: z.number().int(),
    currency: z.string(),
    billableMinutes: z.number().optional(),
    ratePerMinute: z.number().optional(),
    ratePerChat: z.number().optional(),
    params: z.union([
      z.object({ duration: z.number() }),
      z.object({ units: z.number().int() }),
    ]),
  }),
});

// ============================================================================
// Usage Schemas
// ============================================================================

export const usageQuerySchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  projectId: z.string().uuid().optional(),
});

export const usageReportSchema = z.object({
  orgId: z.string(),
  projectId: z.string().nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  transcriptionMinutes: z.number(),
  transcriptionCost: z.number(),
  analysisTokensIn: z.number().int(),
  analysisTokensOut: z.number().int(),
  analysisCost: z.number(),
  storageGbMonths: z.number(),
  storageCost: z.number(),
  emailRequests: z.number().int(),
  emailCost: z.number(),
  webhookRequests: z.number().int(),
  webhookCost: z.number(),
  totalCost: z.number(),
});

export const usageResponseSchema = z.object({
  usageReport: usageReportSchema,
});

// ============================================================================
// Ledger Schemas
// ============================================================================

export const ledgerQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  /**
   * Продолжить ПОСЛЕ этой строки: ISO-метка и id последней показанной.
   *
   * Курсор, а не `offset`, потому что журнал живой. Между двумя страницами в
   * него дописываются списания, каждое новее уже показанных, и они сдвигают
   * всё окно: строка с границы приходит второй раз, а её соседка не приходит
   * никогда. `offset` остаётся для прямых вызовов API — экран биллинга ходит
   * курсором.
   *
   * Пара, а не одна метка: `created_at` не уникален (у записей одной
   * транзакции `defaultNow()` даёт одно значение), поэтому `id` доводит
   * сравнение до полного.
   */
  cursorCreatedAt: z.string().datetime().optional(),
  cursorId: z.string().uuid().optional(),
  entryType: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  currency: z.string().length(3).optional(),
});

export const ledgerEntryResponseSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  accountType: z.string(),
  transactionId: z.string().uuid(),
  entryType: z.string(),
  amount: z.string(),
  currency: z.string(),
  description: z.string(),
  metadataJson: z.unknown().nullable(),
  createdAt: z.string(),
});

export const ledgerResponseSchema = z.object({
  entries: z.array(ledgerEntryResponseSchema),
  total: z.number().int(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  entryType: z.string().optional(),
  /**
   * Чем продолжать. Отсутствует, когда страница последняя, — так клиенту не
   * приходится выводить конец списка из сравнения счётчиков, которые
   * считались разными запросами и в разные моменты.
   */
  nextCursor: z
    .object({ createdAt: z.string(), id: z.string() })
    .nullable()
    .default(null),
});

// ============================================================================
// Pricing Plan Schemas (Per-Minute Billing)
// ============================================================================

export const pricingPlanResponseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().nullable(),
  planName: z.string(),
  ratePerMinute: z.number(),
  ratePerChat: z.number(),
  currency: z.string(),
  effectiveFrom: z.string(),
  effectiveUntil: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  tier: z.string().nullable(),
  isOverride: z.boolean().optional(),
  source: z.enum(['tier_default', 'org_override']).optional(),
});

export const updatePricingPlanRequestSchema = z
  .object({
    planName: z.string().min(1).optional(),
    ratePerMinute: z.number().nonnegative(),
    ratePerChat: z.number().nonnegative().optional(),
    tier: subscriptionTierSchema.optional(),
    effectiveFrom: z
      .string()
      .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format')
      .optional(),
  })
  .strict();

export const getPricingPlanResponseSchema = z.object({
  success: z.boolean(),
  pricingPlan: pricingPlanResponseSchema.nullable(),
});

export const updatePricingPlanResponseSchema = z.object({
  success: z.boolean(),
  pricingPlan: pricingPlanResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Tier Pricing Schemas
// ============================================================================

export const tierPricingResponseSchema = z.object({
  tier: subscriptionTierSchema,
  ratePerMinute: z.number(),
  ratePerChat: z.number(),
  currency: z.string(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});

export const tierPricingListResponseSchema = z.object({
  success: z.boolean(),
  tiers: z.array(tierPricingResponseSchema),
});

export const updateTierPricingRequestSchema = z
  .object({
    ratePerMinute: z.number().nonnegative().max(100),
    ratePerChat: z.number().nonnegative().max(100),
  })
  .strict();

export const updateTierPricingResponseSchema = z.object({
  success: z.boolean(),
  tier: tierPricingResponseSchema,
});

// ============================================================================
// Usage Summary Schemas (Per-Minute Billing - Client-Facing)
// ============================================================================

export const perMinuteUsageSummarySchema = z.object({
  totalMinutes: z.number(),
  totalChats: z.number().int(),
  totalChargeCents: z.number().int(),
  totalRecords: z.number().int(),
  ratePerMinute: z.number(),
  ratePerChat: z.number(),
  /**
   * Promotional minutes still available (offer layer). Reported separately from
   * included and paid minutes so the UI can show the gift as its own line —
   * folding it into either would hide it from the customer.
   */
  bonusMinutes: z.number().default(0),
  periodStart: z.string(),
  periodEnd: z.string(),
});

export const perMinuteUsageSummaryResponseSchema = z.object({
  usage: perMinuteUsageSummarySchema,
});

// ============================================================================
// Margin Report Schemas (Admin-Only)
// ============================================================================

export const marginBreakdownItemSchema = z.object({
  type: z.string(),
  providerCostCents: z.number().int(),
});

/**
 * One org's margin. Revenue is the single definition both admin reports use
 * (#3059): usage + paid Stripe subscription, wallet top-ups excluded — the
 * same figure the org's P&L row shows for the period.
 */
export const marginReportSchema = z.object({
  revenueCents: z.number().int(),
  usageCents: z.number().int(),
  subscriptionCents: z.number().int(),
  totalProviderCostCents: z.number().int(),
  marginCents: z.number().int(),
  marginPercent: z.number(),
  breakdown: z.array(marginBreakdownItemSchema),
  periodStart: z.string(),
  periodEnd: z.string(),
});

export const marginReportResponseSchema = z.object({
  success: z.boolean(),
  margin: marginReportSchema,
});

// ============================================================================
// Per-Org Financial Report (P&L) Schemas (Admin-Only)
// GET /admin/reports/financial/orgs
// ============================================================================

export const orgFinancialReportRowSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  records: z.number().int(),
  minutes: z.number(),
  tokensIn: z.number(),
  tokensOut: z.number(),
  /** Recognized revenue — usage plus subscription — in integer cents. */
  revenueCents: z.number().int(),
  /** The subscription part of `revenueCents`, in integer cents. */
  subscriptionCents: z.number().int(),
  /** Wallet top-ups (manual + Stripe), in integer cents — NOT revenue. */
  paymentsCents: z.number().int(),
  providerCostCents: z.number().int(),
  marginCents: z.number().int(),
  marginPercent: z.number(),
  balanceCents: z.number().int(),
});

export const orgFinancialReportTotalsSchema = z.object({
  records: z.number().int(),
  minutes: z.number(),
  revenueCents: z.number().int(),
  subscriptionCents: z.number().int(),
  paymentsCents: z.number().int(),
  providerCostCents: z.number().int(),
  marginPercent: z.number(),
});

export const orgFinancialReportSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  organizations: z.array(orgFinancialReportRowSchema),
  totals: orgFinancialReportTotalsSchema,
});

export const orgFinancialReportResponseSchema = z.object({
  success: z.boolean(),
  data: orgFinancialReportSchema,
});

// ============================================================================
// Per-Minute Billing Summary Schema (Enhanced)
// ============================================================================

export const perMinuteBillingSummaryResponseSchema = z.object({
  dataAvailable: z.boolean().optional(),
  /**
   * The balance was read, even when `dataAvailable` is false: it is watched
   * apart from the other reads, and one slow neighbour no longer throws it
   * away (#1097). Absent from an older API — then the balance is as known as
   * the summary is.
   */
  balanceAvailable: z.boolean().optional(),
  balance: z.object({
    amount: z.number(),
    currency: z.string(),
  }),
  usage: z.object({
    totalMinutes: z.number(),
    totalChats: z.number().int(),
    totalChargeCents: z.number().int(),
    /**
     * Dollars actually charged for chats in the window. Reported on its own
     * because chats can no longer be priced by multiplication: a chat spends a
     * grant minute, so one covered by the grant costs $0 and a partly covered
     * one costs a fraction of the rate. `.default(0)` keeps an older API
     * deployment from failing client-side validation outright — the field is
     * new, and this response is parsed strictly enough to throw.
     */
    chatChargeAmount: z.number().default(0),
    ratePerMinute: z.number(),
    ratePerChat: z.number(),
    // Trailing 14-day customer-charge total (cents, usage_charges basis) —
    // same basis as `balance`, independent of periodStart/periodEnd. Drives
    // the frontend's runway estimate (balance / avgDailyBurn).
    last14DaysChargeCents: z.number().int(),
    /** Promotional minutes still available (offer layer), shown as its own line. */
    bonusMinutes: z.number().default(0),
    /**
     * Как минуты периода разложились по котлам — грант тарифа, бонус,
     * перерасход, — прочитанные из сплита, записанного в момент списания
     * (`usage_charges.metadata_json`). Экран не имеет права выводить это
     * арифметикой: `computeMinuteSplit` допускает небольшой перегрант при
     * параллельной обработке, и выведенное число разошлось бы с оплаченным.
     *
     * `unsplitMinutes` — минуты записей БЕЗ сплита: postpaid-организации
     * (грантовой логики нет вовсе) и строки, записанные до появления сплита.
     * Отдельно, чтобы три нуля не выдали их за «покрытые грантом».
     *
     * `null` — разбивки НЕТ: не просили (`includeMinuteSplit` не выставлен),
     * не сосчиталась или не успела в свой предел. Отличать это от четырёх
     * нулей обязательно: нули означают «сосчитали, и всё по нулям», и рядом с
     * ненулевым расходом читаются как факт, а не как отказ. `null` целиком и
     * `.default(null)`: поле новое, а ответ парсится строго — без умолчания
     * старый деплой API уронил бы клиентскую валидацию.
     */
    minuteSplit: z
      .object({
        grantMinutes: z.number(),
        bonusMinutes: z.number(),
        overageMinutes: z.number(),
        unsplitMinutes: z.number(),
      })
      .nullable()
      .default(null),
  }),
  /**
   * Как организация платит: `prepaid` — кошелёк и грант включённых минут,
   * `postpaid` — контрактный Enterprise.
   *
   * Экрану это нужно ровно затем, чтобы не выдать чужое за грант. У postpaid
   * `createPerMinuteCharge` не считает сплит вовсе и пишет каждую строку без
   * него, а тарифный грант Enterprise при этом равен -1 («без лимита») — то
   * есть без этого поля карточка печатает «тариф без лимита» над минутами,
   * за которые взяли контрактную ставку.
   *
   * `null` — режим НЕ ИЗВЕСТЕН: строки в D1 нет или D1 не ответила. Подставить
   * здесь `prepaid` «как у колонки» — тот же дефект с другой стороны: Postgres
   * при сбое D1 отвечает нормально, `dataAvailable` остаётся true, и
   * контрактная организация снова получает грантовые подписи. Потребитель
   * обязан различать «prepaid» и «не знаю».
   */
  billingMode: z.enum(['prepaid', 'postpaid']).nullable().default(null),
  budget: z
    .object({
      limitCents: z.number().int(),
      usedCents: z.number().int(),
      remainingCents: z.number().int(),
      warningThresholdPercent: z.number().int(),
    })
    .nullable(),
});

// ============================================================================
// Costs Schemas
// ============================================================================

export const costItemSchema = z.object({
  id: z.string().uuid(),
  costType: costTypeSchema,
  provider: z.string(),
  costCents: z.number().int(),
  count: z.number().int(),
});

export const costsResponseSchema = z.object({
  items: z.array(costItemSchema),
  total: z.number().int(),
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type CreditSource = z.infer<typeof creditSourceSchema>;
export type CostType = z.infer<typeof costTypeSchema>;
export type OperationType = z.infer<typeof operationTypeSchema>;

export type CreateManualCreditRequest = z.infer<typeof createManualCreditRequestSchema>;
export type ManualCreditResponse = z.infer<typeof manualCreditResponseSchema>;
export type UpdateBudgetLimitRequest = z.infer<typeof updateBudgetLimitRequestSchema>;
export type BudgetLimitResponse = z.infer<typeof budgetLimitResponseSchema>;
export type UsageReport = z.infer<typeof usageReportSchema>;
export type LedgerEntryResponse = z.infer<typeof ledgerEntryResponseSchema>;
export type CostItem = z.infer<typeof costItemSchema>;

// Frontend-compatible type exports (wrapper types)
export type BudgetLimit = z.infer<typeof budgetLimitResponseSchema>;
export type AccountBalance = z.infer<typeof balanceResponseSchema>['balance'];
export type LedgerEntry = z.infer<typeof ledgerEntryResponseSchema>;
export type BillingBudgetResponse = z.infer<typeof getBudgetResponseSchema>;
export type BillingBalanceResponse = z.infer<typeof balanceResponseSchema>;
export type BillingLedgerResponse = z.infer<typeof ledgerResponseSchema>;
export type BillingUsageResponse = z.infer<typeof usageResponseSchema>;

// Tier pricing type exports
export type TierPricingResponse = z.infer<typeof tierPricingResponseSchema>;
export type UpdateTierPricingRequest = z.infer<typeof updateTierPricingRequestSchema>;

// Per-minute billing type exports
export type PricingPlanResponse = z.infer<typeof pricingPlanResponseSchema>;
export type UpdatePricingPlanRequest = z.infer<typeof updatePricingPlanRequestSchema>;
export type PerMinuteUsageSummary = z.infer<typeof perMinuteUsageSummarySchema>;
export type MarginReport = z.infer<typeof marginReportSchema>;
export type MarginBreakdownItem = z.infer<typeof marginBreakdownItemSchema>;
export type PerMinuteBillingSummary = z.infer<
  typeof perMinuteBillingSummaryResponseSchema
>;
export type GetPricingPlanResponse = z.infer<typeof getPricingPlanResponseSchema>;
export type UpdatePricingPlanResponse = z.infer<typeof updatePricingPlanResponseSchema>;
export type MarginReportResponse = z.infer<typeof marginReportResponseSchema>;
export type OrgFinancialReportRow = z.infer<typeof orgFinancialReportRowSchema>;
export type OrgFinancialReportTotals = z.infer<typeof orgFinancialReportTotalsSchema>;
export type OrgFinancialReport = z.infer<typeof orgFinancialReportSchema>;
export type OrgFinancialReportResponse = z.infer<typeof orgFinancialReportResponseSchema>;

// ============================================================================
// Auto-Recharge Schemas (prepaid wallet top-up)
// ============================================================================
//
// When the prepaid wallet balance drops below `thresholdCents`, the org's saved
// card is charged off-session for `rechargeAmountCents` and the wallet credited.
// Persisted in `orgs.settingsJson.autoRecharge`. System-managed fields
// (paymentMethodId, status, counters) are NOT client-editable — see
// `updateAutoRechargeRequestSchema` for the editable subset.
// Design: docs/plans/2026-06-12-auto-recharge-design.md

export const autoRechargeStatusSchema = z.enum([
  'active', // armed; will charge when balance < threshold
  'paused_no_card', // enabled but no saved payment method
  'paused_failures', // auto-paused after consecutive charge failures
  'needs_action', // last charge needs customer authentication (3DS)
]);

/** Decline/failure detail recorded on the last failed attempt. */
export const autoRechargeFailureSchema = z.object({
  at: z.string().datetime(),
  code: z.string(),
  declineCode: z.string().nullable().default(null),
  message: z.string(),
});

/** Full persisted config (orgs.settingsJson.autoRecharge). */
export const autoRechargeConfigSchema = z.object({
  enabled: z.boolean().default(false),
  thresholdCents: z.number().int().min(500).max(100000).default(1000), // $5–$1000, default $10
  rechargeAmountCents: z.number().int().min(1000).max(500000).default(2500), // $10–$5000, default $25
  paymentMethodId: z.string().nullable().default(null),
  status: autoRechargeStatusSchema.default('paused_no_card'),
  consecutiveFailures: z.number().int().min(0).default(0),
  cooldownUntil: z.string().datetime().nullable().default(null),
  pendingPaymentIntentId: z.string().nullable().default(null),
  lastAttemptAt: z.string().datetime().nullable().default(null),
  lastSuccessAt: z.string().datetime().nullable().default(null),
  lastFailure: autoRechargeFailureSchema.nullable().default(null),
  // Monotonic per-episode counter. Drives the Stripe idempotency key so that
  // every concurrent/boundary trigger within one low-balance episode collapses
  // onto a single charge, while a later episode (post-resolution) gets a fresh
  // key. Bumped on every resolution (success or failure).
  rechargeGeneration: z.number().int().min(0).default(0),
});

/** Client-editable subset (PUT body). */
export const updateAutoRechargeRequestSchema = z
  .object({
    enabled: z.boolean(),
    thresholdCents: z.number().int().min(500).max(100000),
    rechargeAmountCents: z.number().int().min(1000).max(500000),
  })
  .strict()
  .refine((d) => d.rechargeAmountCents > d.thresholdCents, {
    message: 'Recharge amount must be greater than the threshold',
    path: ['rechargeAmountCents'],
  });

/** Saved card summary (never exposes the raw payment method id to the client). */
export const autoRechargeCardSchema = z.object({
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number().int(),
  expYear: z.number().int(),
});

/** Client-safe response view. */
export const autoRechargeResponseSchema = z.object({
  enabled: z.boolean(),
  thresholdCents: z.number().int(),
  rechargeAmountCents: z.number().int(),
  status: autoRechargeStatusSchema,
  hasPaymentMethod: z.boolean(),
  card: autoRechargeCardSchema.nullable(),
  consecutiveFailures: z.number().int(),
  cooldownUntil: z.string().datetime().nullable(),
  lastSuccessAt: z.string().datetime().nullable(),
  lastFailure: autoRechargeFailureSchema.nullable(),
});

/** GET/PUT envelope returned by the auto-recharge routes. */
export const autoRechargeEnvelopeSchema = z.object({
  success: z.boolean(),
  autoRecharge: autoRechargeResponseSchema,
});

/** Request to start card collection — hosted Checkout redirect URLs. */
export const autoRechargeSetupRequestSchema = z
  .object({
    successUrl: safeRedirectUrl,
    cancelUrl: safeRedirectUrl,
  })
  .strict();

/** Hosted setup-mode Checkout URL to redirect the customer to. */
export const autoRechargeSetupResponseSchema = z.object({
  checkoutUrl: z.string().url(),
});

export type AutoRechargeStatus = z.infer<typeof autoRechargeStatusSchema>;
export type AutoRechargeFailure = z.infer<typeof autoRechargeFailureSchema>;
export type AutoRechargeConfig = z.infer<typeof autoRechargeConfigSchema>;
export type UpdateAutoRechargeRequest = z.infer<typeof updateAutoRechargeRequestSchema>;
export type AutoRechargeCard = z.infer<typeof autoRechargeCardSchema>;
export type AutoRechargeResponse = z.infer<typeof autoRechargeResponseSchema>;
export type AutoRechargeEnvelope = z.infer<typeof autoRechargeEnvelopeSchema>;
export type AutoRechargeSetupRequest = z.infer<typeof autoRechargeSetupRequestSchema>;
export type AutoRechargeSetupResponse = z.infer<typeof autoRechargeSetupResponseSchema>;

// ============================================================================
// Pause-Recovery Offer (offer layer, Phase 2)
// ============================================================================

/**
 * State of the one-time "upgrade within 72 hours" offer.
 *
 * `expiresAt` is derived server-side from the real pause event, so the client
 * renders a countdown it cannot invent: with no anchor both timestamps are null
 * and `active` is false.
 */
export const pauseOfferSchema = z.object({
  active: z.boolean(),
  pausedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  bonusMinutes: z.number(),
  alreadyGranted: z.boolean(),
  /**
   * Whether analysis is STILL stopped right now.
   *
   * The offer survives a wallet top-up — the bonus is earned by upgrading, not
   * by paying for overage — but the sentence "analysis is paused" does not. A
   * free org that topped up is processing records again while the offer window
   * runs on, so the banner has to be told which of the two it is looking at.
   */
  stillPaused: z.boolean(),
  /**
   * What is paying for analysis right now — `null` while it is stopped.
   *
   * `stillPaused` alone could not tell the ways of resuming apart, so the
   * banner said "analysis runs off your balance" to an org whose balance was
   * $0.00 and whose records were being covered by bonus minutes. They are
   * paid for differently and read differently: money runs down, a gift does
   * not, and telling a customer we are charging them when we are not is the
   * kind of sentence they screenshot.
   *
   * `grant` is the third pool, and it was missing entirely: an org whose
   * billing cycle renewed pays with its own included minutes, and the server
   * — reading only the wallet and the bonus — answered "still paused" while
   * the pipeline was processing that org's records (#500). `contract` covers
   * postpaid and unlimited-grant orgs, which no pool gates at all.
   *
   * The server now derives this from `evaluateBalanceGate`, the same function
   * the pipeline gate and the public-ingest pre-check call, so the sentence on
   * the banner and the decision in the pipeline cannot disagree again.
   */
  coveredBy: z.enum(['grant', 'bonus', 'balance', 'contract']).nullish(),
  /**
   * Minutes of the current cycle's included grant still unspent, when the
   * grant is what is covering analysis. `null` in every other case.
   */
  grantMinutesLeft: z.number().nullish(),
  /**
   * Promotional minutes still unspent, when they are what is covering
   * analysis. `null` in every other case: an org paid for another way has no
   * bonus figure to report and a stopped one has nothing left at all — a
   * display choice, not an unknown.
   */
  bonusMinutesLeft: z.number().nullish(),
});

export const pauseOfferResponseSchema = z.object({
  success: z.boolean(),
  offer: pauseOfferSchema,
});

export type PauseOffer = z.infer<typeof pauseOfferSchema>;
export type PauseOfferResponse = z.infer<typeof pauseOfferResponseSchema>;

/**
 * State of the one-time first-top-up offer, shown on the top-up screen before
 * payment. The threshold and bonus size come from the server so the promise on
 * screen and the grant after payment cannot drift apart.
 */
export const firstTopupOfferSchema = z.object({
  available: z.boolean(),
  bonusMinutes: z.number(),
  minAmountCents: z.number(),
});

export const firstTopupOfferResponseSchema = z.object({
  success: z.boolean(),
  offer: firstTopupOfferSchema,
});

export type FirstTopupOffer = z.infer<typeof firstTopupOfferSchema>;
export type FirstTopupOfferResponse = z.infer<typeof firstTopupOfferResponseSchema>;

/**
 * Nightly plan recommendation, shown as a banner on the billing page.
 *
 * `available: false` is the normal case — most orgs are on the right plan, and
 * the advisor stays quiet rather than inventing a reason to talk. The saving is
 * a monthly dollar amount rather than a percentage, so a customer can check it
 * against an invoice.
 */
export const tierAdviceSchema = z.discriminatedUnion('available', [
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    kind: z.enum(['upgrade', 'downgrade']),
    fromTier: z.string(),
    toTier: z.string(),
    monthlySavingsCents: z.number(),
    avgMinutes: z.number(),
    issuedAt: z.string().nullable(),
  }),
]);

export const tierAdviceResponseSchema = z.object({
  success: z.boolean(),
  advice: tierAdviceSchema,
});

export type TierAdvice = z.infer<typeof tierAdviceSchema>;
export type TierAdviceResponse = z.infer<typeof tierAdviceResponseSchema>;
